// ─────────────────────────────────────────
// ts/contact.ts — Contact form logic
// Validation, submission, visual feedback, accessibility.
// Tauvior Landing Page
// ─────────────────────────────────────────

import { getLocale, getTranslation } from '../i18n/index';
import {
  CONTACT_EMAIL,
  MESSAGE_MIN_LENGTH,
  FEEDBACK_AUTO_HIDE_MS,
} from './env';
import { sendContactMail, type MailPayload } from './mail';

// ── Types ─────────────────────────────────

interface FormFields {
  name:    HTMLInputElement;
  email:   HTMLInputElement;
  company: HTMLInputElement;
  message: HTMLTextAreaElement;
}

/** Valores estáveis do assunto — os mesmos em qualquer idioma */
const INTEREST_VALUES = ['grid', 'advisory', 'outro'] as const;

interface FormMessages {
  success:      string;
  errorGeneral: string;
  errorCaptcha: string;
  errorName:    string;
  errorEmail:   string;
  errorMessage: string;
  loadingCaptcha: string;
  sending:      string;
  submitLabel:  string;
}

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

// ── i18n feedback messages ─────────────────────────────────────────────────

function buildMessages(): FormMessages {
  const locale = getLocale();
  const email  = CONTACT_EMAIL;

  // O rótulo do botão vive nos JSONs de tradução — duplicá-lo aqui já fez
  // o botão divergir do resto da página depois de uma troca de copy.
  const submitLabel = getTranslation().contact.form.submitBtn;

  const map: Record<string, FormMessages> = {
    'pt-BR': {
      success:      '✓  Mensagem enviada com sucesso. Retornaremos em até 24 horas.',
      errorGeneral: `✗  Algo deu errado. Tente novamente ou envie para ${email}`,
      errorCaptcha: '✗  Falha ao validar a verificacao anti-spam. Revise a chave reCAPTCHA v3 e o dominio cadastrado.',
      errorName:    'Por favor, informe seu nome.',
      errorEmail:   'Por favor, informe um e-mail válido.',
      errorMessage: `A mensagem deve ter ao menos ${MESSAGE_MIN_LENGTH} caracteres.`,
      loadingCaptcha: 'Validando verificacao anti-spam…',
      sending:      'Enviando…',
      submitLabel,
    },
    'en-US': {
      success:      '✓  Message sent successfully. We will get back to you within 24 hours.',
      errorGeneral: `✗  Something went wrong. Please try again or email ${email}`,
      errorCaptcha: '✗  Failed to validate the anti-spam check. Review your reCAPTCHA v3 key and allowed domains.',
      errorName:    'Please enter your name.',
      errorEmail:   'Please enter a valid email address.',
      errorMessage: `The message must be at least ${MESSAGE_MIN_LENGTH} characters.`,
      loadingCaptcha: 'Validating anti-spam check…',
      sending:      'Sending…',
      submitLabel,
    },
  };

  return map[locale] ?? map['pt-BR'];
}

function getUserFacingError(err: unknown): string {
  const fallback = buildMessages().errorGeneral;

  if (!(err instanceof Error)) return fallback;

  const message = err.message.trim();
  if (!message) return fallback;

  return message;
}

// ── DOM helpers ────────────────────────────

function getFormFields(form: HTMLFormElement): FormFields {
  return {
    name:    form.querySelector<HTMLInputElement>('[name="name"]')!,
    email:   form.querySelector<HTMLInputElement>('[name="email"]')!,
    company: form.querySelector<HTMLInputElement>('[name="company"]')!,
    message: form.querySelector<HTMLTextAreaElement>('[name="message"]')!,
  };
}

function getInterestSelect(form: HTMLFormElement): HTMLSelectElement | null {
  return form.querySelector<HTMLSelectElement>('select[name="interest"]');
}

/** O rótulo traduzido da opção escolhida — é o que chega no e-mail. */
function getInterestLabel(form: HTMLFormElement): string {
  const select = getInterestSelect(form);
  if (!select || !select.value) return '';
  return select.selectedOptions[0]?.textContent?.trim() ?? select.value;
}

function getRecaptchaV3SiteKey(form: HTMLFormElement): string {
  return form.dataset.recaptchaV3SiteKey?.trim() ?? '';
}

function setFieldError(
  input: HTMLInputElement | HTMLTextAreaElement,
  message: string,
): void {
  const wrapper = input.closest<HTMLElement>('.form-field');
  if (!wrapper) return;

  // Mark field visually
  input.classList.add('has-error');
  input.setAttribute('aria-invalid', 'true');

  const errorEl = wrapper.querySelector<HTMLElement>('.field-error');
  if (errorEl) {
    errorEl.textContent = message;
    // Link error to input for screen readers
    const id = `err-${input.name}`;
    errorEl.id = id;
    input.setAttribute('aria-describedby', id);
  }
}

function clearFieldError(input: HTMLInputElement | HTMLTextAreaElement): void {
  input.classList.remove('has-error');
  input.removeAttribute('aria-invalid');
  input.removeAttribute('aria-describedby');

  const wrapper = input.closest<HTMLElement>('.form-field');
  const errorEl = wrapper?.querySelector<HTMLElement>('.field-error');
  if (errorEl) errorEl.textContent = '';
}

function clearAllErrors(form: HTMLFormElement): void {
  const fields = form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea');
  fields.forEach(clearFieldError);
}

function showFeedback(
  el: HTMLElement,
  message: string,
  type: 'success' | 'error',
  options: { scrollIntoView?: boolean } = {},
): void {
  const { scrollIntoView = true } = options;
  el.textContent   = message;
  el.dataset.type  = type;
  el.style.display = 'block';
  if (scrollIntoView) {
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  if (type === 'success') {
    setTimeout(() => { el.style.display = 'none'; }, FEEDBACK_AUTO_HIDE_MS);
  }
}

function setSubmitState(btn: HTMLButtonElement, loading: boolean, label: string): void {
  btn.disabled      = loading;
  btn.textContent   = label;
  btn.style.opacity = loading ? '0.6' : '';
  btn.style.cursor  = loading ? 'not-allowed' : '';
}

function ensureRecaptchaV3Script(siteKey: string): Promise<void> {
  if (window.grecaptcha) return Promise.resolve();

  const existing = document.querySelector<HTMLScriptElement>('script[data-recaptcha-v3-script="true"]');
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA v3 script.')), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.dataset.recaptchaV3Script = 'true';
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA v3 script.')), { once: true });
    document.head.appendChild(script);
  });
}

async function getRecaptchaV3Token(form: HTMLFormElement): Promise<string> {
  const siteKey = getRecaptchaV3SiteKey(form);
  if (!siteKey || siteKey === 'PASTE_YOUR_RECAPTCHA_V3_SITE_KEY_HERE') {
    throw new Error(buildMessages().errorCaptcha);
  }

  await ensureRecaptchaV3Script(siteKey);

  if (!window.grecaptcha) {
    throw new Error(buildMessages().errorCaptcha);
  }

  return new Promise((resolve, reject) => {
    window.grecaptcha?.ready(() => {
      window.grecaptcha?.execute(siteKey, { action: 'submit' })
        .then((token) => {
          if (!token) {
            reject(new Error(buildMessages().errorCaptcha));
            return;
          }

          resolve(token);
        })
        .catch(() => reject(new Error(buildMessages().errorCaptcha)));
    });
  });
}

// ── Validation ─────────────────────────────

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function validate(fields: FormFields): boolean {
  const msg   = buildMessages();
  let   valid = true;

  if (!fields.name.value.trim()) {
    setFieldError(fields.name, msg.errorName);
    valid = false;
  }

  if (!isValidEmail(fields.email.value)) {
    setFieldError(fields.email, msg.errorEmail);
    valid = false;
  }

  if (fields.message.value.trim().length < MESSAGE_MIN_LENGTH) {
    setFieldError(fields.message, msg.errorMessage);
    valid = false;
  }

  // Focus first invalid field
  if (!valid) {
    const firstError = Object.values(fields).find(
      (f) => f.classList.contains('has-error'),
    );
    firstError?.focus();
  }

  return valid;
}

// ── Submission ─────────────────────────────

async function handleSubmit(
  form:        HTMLFormElement,
  fields:      FormFields,
  submitBtn:   HTMLButtonElement,
  feedbackEl:  HTMLElement,
): Promise<void> {
  const msg = buildMessages();

  setSubmitState(submitBtn, true, msg.loadingCaptcha);

  let recaptchaToken = '';

  try {
    recaptchaToken = await getRecaptchaV3Token(form);
  } catch (err) {
    console.error(err);
    showFeedback(feedbackEl, getUserFacingError(err), 'error');
    setSubmitState(submitBtn, false, msg.submitLabel);
    return;
  }

  setSubmitState(submitBtn, true, msg.sending);

  const payload: MailPayload = {
    name:    fields.name.value.trim(),
    email:   fields.email.value.trim(),
    company: fields.company.value.trim(),
    interest: getInterestLabel(form),
    message: fields.message.value.trim(),
    locale:  getLocale(),
    source:  window.location.hostname,
    recaptchaToken,
  };

  try {
    await sendContactMail(payload);
    form.reset();
    showFeedback(feedbackEl, msg.success, 'success');
  } catch (err) {
    console.error(err);
    showFeedback(feedbackEl, getUserFacingError(err), 'error');
  } finally {
    setSubmitState(submitBtn, false, msg.submitLabel);
  }
}

// ── Live validation ─────────────────────────

function bindLiveValidation(fields: FormFields): void {
  // Clear field error as the user starts correcting it
  Object.values(fields).forEach((field) => {
    field.addEventListener('input', () => clearFieldError(field));
  });

  // Real-time email format check on blur
  fields.email.addEventListener('blur', () => {
    const val = fields.email.value.trim();
    if (val && !isValidEmail(val)) {
      setFieldError(fields.email, buildMessages().errorEmail);
    }
  });
}

// ── Character counter for message ──────────

function bindMessageCounter(textarea: HTMLTextAreaElement): void {
  const wrapper = textarea.closest<HTMLElement>('.form-field');
  if (!wrapper) return;

  const counter = document.createElement('span');
  counter.className   = 'char-counter';
  counter.setAttribute('aria-live', 'polite');
  wrapper.appendChild(counter);

  // "0 / 20 min" era ambíguo (minutos?): o rótulo diz que o mínimo é de caracteres.
  const pt = getLocale() === 'pt-BR';

  const update = (): void => {
    const len      = textarea.value.trim().length;
    const min      = MESSAGE_MIN_LENGTH;
    const ok       = len >= min;
    counter.textContent = ok
      ? (pt ? `${len} caracteres` : `${len} characters`)
      : (pt ? `${len} / mín. ${min} caracteres` : `${len} / min. ${min} characters`);
    counter.dataset.ok = String(ok);
  };

  textarea.addEventListener('input', update);
  update();
}

// ── Public init ────────────────────────────

/**
 * Wires the contact form.
 * Safe to call even when the form is absent from the DOM.
 */
export function initContactForm(): void {
  const form = document.getElementById('contact-form') as HTMLFormElement | null;
  if (!form) return;

  const fields    = getFormFields(form);
  const submitBtn = form.querySelector<HTMLButtonElement>('[type="submit"]')!;
  const feedback  = form.querySelector<HTMLElement>('.form-feedback')!;
  const msg       = buildMessages();

  // Restore translated submit label (renderer sets it; keep in sync after reset)
  submitBtn.textContent = msg.submitLabel;

  bindLiveValidation(fields);
  bindMessageCounter(fields.message);

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    clearAllErrors(form);
    feedback.style.display = 'none';

    if (validate(fields)) {
      await handleSubmit(form, fields, submitBtn, feedback);
    }
  });
}

// ── Assunto pré-selecionado ────────────────

function presetInterest(value: string | undefined): void {
  if (!value || !(INTEREST_VALUES as readonly string[]).includes(value)) return;
  const form = document.getElementById('contact-form') as HTMLFormElement | null;
  const select = form ? getInterestSelect(form) : null;
  if (select) select.value = value;
}

/**
 * Quem clica em "Conversar sobre Advisory" não deveria ter de escolher
 * Advisory de novo no formulário. Duas entradas preenchem o assunto:
 *
 * - `?interesse=grid` na URL — para links vindos de outras páginas;
 * - `data-interest="advisory"` em qualquer botão ou link da própria página.
 */
export function initInterestPreset(): void {
  const params = new URLSearchParams(window.location.search);
  presetInterest(params.get('interesse') ?? undefined);
  const select = document.querySelector<HTMLSelectElement>('#field-interest');
  const cards = document.querySelectorAll<HTMLButtonElement>('.path-card[data-interest]');
  const syncCards = (): void => {
    cards.forEach((card) => card.setAttribute('aria-pressed', String(card.dataset.interest === select?.value)));
  };
  syncCards();
  select?.addEventListener('change', syncCards);
  document.getElementById('contact-form')?.addEventListener('reset', () => requestAnimationFrame(syncCards));

  document.querySelectorAll<HTMLElement>('[data-interest]').forEach((el) => {
    el.addEventListener('click', () => {
      presetInterest(el.dataset.interest);
      syncCards();
      if (el.matches('.path-card')) {
        document.getElementById('contact-form')?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
        document.getElementById('field-name')?.focus({ preventScroll: true });
      }
    });
  });
}
